import { Button, Form, Input, Modal, Select, Space, message } from "antd";
import { useState } from "react";
import { transportApi } from "../../api/transportApi";
import { getApiErrorMessage } from "../../utils/apiError";
import { getDefaultContext } from "../../api/httpClient";
import { getEnteredByUser } from "../../utils/currentUser";
import type { LookupItem } from "./LookupModal";

const VEHICLE_TYPES = [
  "Open Truck", "Closed Truck", "Container Truck", "Trailer",
  "Tanker", "Refrigerated Truck", "Mini Truck", "Pickup", "Tempo", "Other",
];

const OWNERSHIP_TYPES = [
  { value: "CompanyOwned", label: "Company Owned" },
  { value: "VendorOwned",  label: "Vendor / Owner Operator" },
  { value: "Rental",       label: "Rental" },
];

interface VehicleCreateModalProps {
  open: boolean;
  prefillName?: string;
  onClose: () => void;
  onCreated: (item: LookupItem) => void;
}

export const VehicleCreateModal = ({
  open,
  prefillName,
  onClose,
  onCreated,
}: VehicleCreateModalProps) => {
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);

  const handleAfterOpen = (visible: boolean) => {
    if (visible) {
      form.resetFields();
      form.setFieldsValue({ vehicleType: "Open Truck", ownershipType: "CompanyOwned" });
      if (prefillName) form.setFieldValue("vehicleNo", prefillName.toUpperCase());
    }
  };

  const handleSave = async () => {
    let values: any;
    try { values = await form.validateFields(); }
    catch { return; }

    const ctx = getDefaultContext();
    setSaving(true);
    try {
      const created = await transportApi.createVehicle({
        ...ctx,
        enteredBy: getEnteredByUser(),
        vehicleNo: values.vehicleNo.toUpperCase(),
        vehicleType: values.vehicleType,
        ownershipType: values.ownershipType,
        capacity: 0,
        fuelType: "Diesel",
        isActive: true,
        isTdsApplicable: false,
        hasRC: false,
        hasForm15I: false,
      });
      const item: LookupItem = {
        id: created.id,
        label: created.vehicleNo,
        subtitle: created.vehicleType,
      };
      message.success(`Vehicle "${created.vehicleNo}" created`);
      onCreated(item);
      form.resetFields();
    } catch (err) {
      message.error(getApiErrorMessage(err, "Unable to create vehicle"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      title="Add New Vehicle"
      open={open}
      onCancel={onClose}
      afterOpenChange={handleAfterOpen}
      destroyOnClose
      width={480}
      footer={
        <Space style={{ justifyContent: "flex-end", width: "100%" }}>
          <Button onClick={onClose}>Cancel</Button>
          <Button type="primary" loading={saving} onClick={() => void handleSave()}>
            Save Vehicle
          </Button>
        </Space>
      }
    >
      <Form form={form} layout="vertical" style={{ marginTop: 8 }}>
        <Form.Item
          name="vehicleNo"
          label="Vehicle / Registration No"
          rules={[{ required: true, message: "Enter vehicle number" }]}
        >
          <Input
            placeholder="e.g. DL01AB1234"
            style={{ textTransform: "uppercase" }}
            onChange={e => form.setFieldValue("vehicleNo", e.target.value.toUpperCase())}
            autoFocus
          />
        </Form.Item>
        <Form.Item
          name="vehicleType"
          label="Vehicle Type"
          rules={[{ required: true }]}
        >
          <Select options={VEHICLE_TYPES.map(v => ({ value: v, label: v }))} />
        </Form.Item>
        <Form.Item
          name="ownershipType"
          label="Ownership"
          rules={[{ required: true }]}
        >
          <Select options={OWNERSHIP_TYPES} />
        </Form.Item>
      </Form>
    </Modal>
  );
};
